package com.eladfinish.bible.contest.screens.americanQuestions

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.screens.americanQuestions.model.AmericanQuestionItem
import com.eladfinish.bible.contest.screens.americanQuestions.model.QuestionResult
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf

/** Per-question state computed once when the question is first shown. */
@Immutable
data class QuestionDisplayState(
    val shuffledOptions: ImmutableList<String>,
    val correctOptionIndex: Int,
    val userAnswerIndex: Int = -1,
)

@Immutable
data class AmericanQuestionsState(
    val isSkipReadMeChecked: Boolean,
    val isLoading: Boolean,
    val dialogState: DialogState,
    val questions: ImmutableList<AmericanQuestionItem>,
    val hebrewGender: HebrewGender,
    // 0-based index into `questions`; -1 = before first question
    val currentQuestionIndex: Int = -1,
    // what is currently displayed (may differ from currentQuestionIndex in history-review mode)
    val displayedQuestionIndex: Int = -1,
    val questionResults: ImmutableList<QuestionResult> = persistentListOf(),
    // parallel list to `questions`; null = question not yet shown
    val questionDisplayStates: ImmutableList<QuestionDisplayState?> = persistentListOf(),
    val isGameOver: Boolean = false,
    val enableCountdown: Boolean = true,
    val usedFiftyFifty: Boolean = false,
    // which option indices (0-3) are hidden by fifty-fifty for the current question
    val disabledOptionIndices: ImmutableList<Int> = persistentListOf(),
    val numberOfQuestions: Int = 10,
    val numberOfQuestionsInFile: Int = 0,
    val remainingNumberOfQuestions: Int = 0,
    val numberOfCorrectAnswers: Int = 0,
    val numberOfAskedQuestions: Int = 0,
) : StateKt {

    val currentQuestion: AmericanQuestionItem?
        get() = questions.getOrNull(currentQuestionIndex)

    val displayedQuestion: AmericanQuestionItem?
        get() = questions.getOrNull(displayedQuestionIndex)

    val currentDisplayState: QuestionDisplayState?
        get() = questionDisplayStates.getOrNull(currentQuestionIndex)

    val displayedDisplayState: QuestionDisplayState?
        get() = questionDisplayStates.getOrNull(displayedQuestionIndex)

    val correctAnswerIndex: Int
        get() = currentDisplayState?.correctOptionIndex ?: -1

    val isCurrentQuestionAnswered: Boolean
        get() = when (questionResults.getOrNull(currentQuestionIndex)) {
            QuestionResult.Correct, QuestionResult.Incorrect -> true
            else -> false
        }

    val effectiveNumberOfQuestions: Int
        get() = minOf(numberOfQuestions, numberOfQuestionsInFile)

    @Stable
    sealed class DialogState {
        data class Help(
            val isDontShowAgainChecked: Boolean,
            val content: String,
            /**
             * True when shown automatically on first launch.
             * In this case, question-loading is deferred until the user dismisses.
             * False when opened manually via the Help toolbar button.
             */
            val isInitialHelp: Boolean = false,
        ) : DialogState()

        data object None : DialogState()
        data object DownloadingQuestions : DialogState()
    }
}

sealed interface AmericanQuestionsAction : ActionKt {
    data object FetchQuestions : AmericanQuestionsAction
    data object FetchFromPairMatching : AmericanQuestionsAction

    /** Builds and shows the help dialog entirely inside the store. */
    data object HelpClicked : AmericanQuestionsAction
    data class ToggleHelp(val shouldShowNextTime: Boolean) : AmericanQuestionsAction
    data object HideDialog : AmericanQuestionsAction
    data object SettingsClicked : AmericanQuestionsAction

    /** Re-reads settings that can change while the screen is open (e.g. countdown toggle). */
    data object SettingsChanged : AmericanQuestionsAction
    data class CopyQuestion(val text: String) : AmericanQuestionsAction

    // game actions
    data class AnswerSelected(val answerIndex: Int) : AmericanQuestionsAction
    data object AdvanceQuestion : AmericanQuestionsAction
    data object NewGame : AmericanQuestionsAction
    data class ShowHistory(val questionIndex: Int) : AmericanQuestionsAction
    data object FiftyFiftyClicked : AmericanQuestionsAction
    data object RefreshClicked : AmericanQuestionsAction
    data class QuestionsReady(
        val questions: List<AmericanQuestionItem>,
        val fileContent: String,
    ) : AmericanQuestionsAction
}

sealed interface AmericanQuestionsEffect : EffectKt {
    data object OpenRandomVerseSettings : AmericanQuestionsEffect

    /** Single effect carrying sound + haptic — avoids DROP_OLDEST race when emitting two effects. */
    data class AnswerResult(val isCorrect: Boolean, val isTimerExpired: Boolean = false) : AmericanQuestionsEffect
    data class SetFiftyFiftyEnabled(val enabled: Boolean) : AmericanQuestionsEffect
    data object CopiedToClipboard : AmericanQuestionsEffect
    data object NoInternet : AmericanQuestionsEffect
}
